import { c as _c } from "react/compiler-runtime";
import type { AccommodationAvailableDate } from '@data-access/graphql/schema.types';
import { useGraphQL } from '@data-access/lib/graphql-query';
import { AvailableDatesDocument } from '@feat/feat-accommodation/src/data-access/documents';
import type { PaxParams } from '@feat/feat-url-state/pax';
import type { DateValue } from '@react-aria/calendar';
import { formatIsoDate } from '@utils/utils-format';
import { differenceInCalendarDays, differenceInDays, eachDayOfInterval, isSameDay, startOfDay } from 'date-fns';
import { addDays, addMonths, subDays } from 'date-fns/fp';
import { mapAccommodationPaxInput } from '../_lib/map-accommodation-pax-input';
type Input = {
  id: number;
  maxLengthOfStay: number | null | undefined;
  paxParams: PaxParams;
  startDate: Date | null;
};
export type Dates = [Date | null, Date | null];
const DEFAULT_MIN_LENGTH_OF_STAY = 1;
const DEFAULT_MAX_LENGTH_OF_STAY = 21;
export type RulesOfStay = {
  minLengthOfStay?: number | null;
  maxLengthOfStay?: number | null;
};
export const useAppAvailableDates = t0 => {
  const $ = _c(20);
  const {
    id,
    maxLengthOfStay,
    startDate,
    paxParams
  } = t0;
  let t1;
  if ($[0] !== paxParams) {
    t1 = mapAccommodationPaxInput(paxParams);
    $[0] = paxParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== id || $[3] !== t1) {
    t2 = {
      input: {
        id,
        allocation: t1
      }
    };
    $[2] = id;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: t3
  } = useGraphQL(AvailableDatesDocument, t2, {
    select: query => {
      if (!query.availableDates) {
        return null;
      }
      const availableDates = filterAvailableDates(query.availableDates);
      const getMinLengthOfStay = dateToCompare => {
        if (!data?.availableDates || !dateToCompare) {
          return 1;
        }
        const rulesOfMinStay = getRulesOfMinStay(availableDates);
        return rulesOfMinStay.get(formatIsoDate(dateToCompare)) ?? DEFAULT_MIN_LENGTH_OF_STAY;
      };
      return {
        minLengthOfStay: getMinLengthOfStay(startDate),
        availableDates: availableDates.map(_temp)
      };
    }
  });
  const data = t3;
  const {
    min,
    max
  } = getInterval(data?.availableDates);
  let t4;
  if ($[5] !== data || $[6] !== max || $[7] !== min) {
    t4 = dates => isValidInterval({
      dates,
      min,
      max,
      availableDates: data?.availableDates
    });
    $[5] = data;
    $[6] = max;
    $[7] = min;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isValid = t4;
  let t5;
  if ($[9] !== data) {
    t5 = date_0 => {
      if (!data?.availableDates) {
        return false;
      }
      return !data.availableDates.includes(date_0.toString());
    };
    $[9] = data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isDateUnavailable = t5;
  const t6 = data?.minLengthOfStay;
  let t7;
  if ($[11] !== maxLengthOfStay || $[12] !== t6) {
    t7 = {
      minLengthOfStay: t6,
      maxLengthOfStay
    };
    $[11] = maxLengthOfStay;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isDateUnavailable || $[15] !== isValid || $[16] !== max || $[17] !== min || $[18] !== t7) {
    t8 = {
      min,
      max,
      isValid,
      isDateUnavailable,
      rulesOfStay: t7
    };
    $[14] = isDateUnavailable;
    $[15] = isValid;
    $[16] = max;
    $[17] = min;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
export const getMinMaxLengthOfStay = (dates: Dates, rulesOfStay: RulesOfStay) => {
  const difference = getDifferenceInDays(dates);
  const [min, max] = getNightCountRange(rulesOfStay);
  const isValid = difference >= min && difference <= max;
  return {
    min,
    max,
    difference,
    isValid
  };
};
const getDifferenceInDays = ([from, to]: Dates) => {
  if (!from || !to) return 0;
  return differenceInCalendarDays(to, from);
};
const getNightCountRange = ({
  minLengthOfStay,
  maxLengthOfStay
}: RulesOfStay) => {
  const min = minLengthOfStay ?? DEFAULT_MIN_LENGTH_OF_STAY;
  const max = maxLengthOfStay ?? DEFAULT_MAX_LENGTH_OF_STAY;
  return [min, max] as const;
};
const filterAvailableDates = (dates: AccommodationAvailableDate[]) => {
  const dataAsDates = dates.map(({
    date
  }) => date);
  return dates.filter((_date, index) => {
    const today = dataAsDates[index];
    const previousDay = dataAsDates[index - 1];
    const nextDay = dataAsDates[index + 1];
    if (!today) return false;
    if (!previousDay && nextDay) {
      return isSameDay(add1Day(today), nextDay);
    }
    if (previousDay && !nextDay) {
      return isSameDay(sub1Day(today), previousDay);
    }
    if (previousDay && nextDay) {
      return isSameDay(sub1Day(today), previousDay) || isSameDay(add1Day(today), nextDay);
    }
  });
};
const getRulesOfMinStay = (dates: AccommodationAvailableDate[]) => {
  const map = new Map<string, number>();
  dates.forEach(({
    date,
    minLengthOfStay
  }) => {
    map.set(formatIsoDate(date), minLengthOfStay);
  });
  return map;
};
const getInterval = (dates: string[] | null | undefined) => {
  return {
    min: addDays(!dates ? 1 : 3, new Date()),
    max: addMonths(12, new Date())
  };
};
const isValidInterval = ({
  dates: [start, end],
  min,
  max,
  availableDates
}: {
  dates: Dates;
  availableDates: string[] | null | undefined;
  min: Date;
  max: Date;
}) => {
  if (!start || !end) return false;
  if (!isSameOrBefore(min, start) || !isSameOrBefore(end, max)) return false;
  if (!availableDates) return true;
  return eachDayOfInterval({
    start,
    end
  }).map(date => formatIsoDate(date)).every(date => availableDates.includes(date));
};
const sub1Day = subDays(1);
const add1Day = addDays(1);
const isSameOrBefore = (date1: Date, date2: Date) => differenceInDays(startOfDay(date1), startOfDay(date2)) <= 0;
function _temp(t0) {
  const {
    date
  } = t0;
  return date;
}